/**
 * Founder loop (I2 / R1) — shared Product Brief UI: the source form (URL / App
 * Store / Play / two sentences + ownership attestation + paste fallback) and the
 * editable brief sections. Used by the onboarding wizard (steps 2–3) and by
 * /strategy-mktg/brief.
 */

import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';

import { WithCreditCost, creditErrorText } from '@/features/credits';
import { useT, type TranslationKey } from '@/i18n';
import { ApiError, describeApiError } from '@/lib/api/client';
import {
  type BriefPrice,
  type BriefSections,
  type BriefSourceType,
  type ExtractResult,
  type FounderGoal,
  type FounderStage,
  FOUNDER_STAGES,
  type ProductBrief,
  detailError,
  extractBrief,
  extractBriefInto,
  updateBriefById,
} from '@/lib/api/founder';

import { ChipToggle, inputClass, primaryBtn, secondaryBtn } from './strategy-ui';

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/** Translate a dynamic sub-key, falling back to the raw value when unknown. */
export function tDyn(t: TFn, base: string, key: string | null | undefined): string {
  if (!key) return '';
  const full = `${base}.${key}` as TranslationKey;
  const out = t(full);
  return out === full ? key : out;
}

/** Localised message for a founder-loop API error (structured detail first). */
export function founderErrorMessage(err: unknown, t: TFn, locale: string): string {
  // An out-of-credits 402 names its real cause inline (the global refill modal
  // carries the CTA); any other error keeps its structured / generic message.
  return creditErrorText(err, founderBaseMessage(err, t, locale));
}

function founderBaseMessage(err: unknown, t: TFn, locale: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown; error?: string } | null;
    const d = body?.detail;
    if (d && typeof d === 'object') {
      const o = d as { message?: string; message_en?: string };
      const msg = locale === 'en' ? o.message_en || o.message : o.message || o.message_en;
      if (msg) return msg;
    }
    if (body?.error === 'feature_not_in_plan') return t('founder.common.planRequired');
    if ([404, 501, 503].includes(err.status) && !detailError(err)) return t('founder.common.unavailable');
  }
  return describeApiError(err, t('founder.common.errorGeneric'));
}

const SOURCE_TYPES: BriefSourceType[] = ['url', 'app_store', 'play_store', 'text'];

export function SectionBox({ title, children, testId, aside }: { title: ReactNode; children: ReactNode; testId?: string; aside?: ReactNode }) {
  return (
    <section className="rounded-token-lg border border-border bg-surface p-4" data-testid={testId}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-muted">
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Stage + goal (onboarding step 1, brief page)
// ---------------------------------------------------------------------------

export function StageGoalFields({
  stage,
  goal,
  onStage,
  onGoal,
  disabled,
}: {
  stage: FounderStage | null;
  goal: FounderGoal;
  onStage: (s: FounderStage) => void;
  onGoal: (g: FounderGoal) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="mb-2 text-xs font-medium text-muted">{t('founder.onboarding.stage.stageLabel')}</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {FOUNDER_STAGES.map((s) => (
            <label
              key={s}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium ${
                stage === s ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted hover:text-foreground'
              }`}
            >
              <input
                type="radio"
                name="founder-stage"
                value={s}
                className="sr-only"
                checked={stage === s}
                disabled={disabled}
                onChange={() => onStage(s)}
                data-testid={`stage-${s}`}
              />
              {t(`founder.stages.${s}` as TranslationKey)}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="founder-goal-metric">{t('founder.onboarding.stage.metricLabel')}</Label>
          <select
            id="founder-goal-metric"
            className={inputClass}
            value={goal.metric}
            disabled={disabled}
            onChange={(e) => onGoal({ ...goal, metric: e.target.value as FounderGoal['metric'] })}
          >
            {(['users', 'mrr', 'other'] as const).map((m) => (
              <option key={m} value={m}>
                {t(`founder.goalMetric.${m}` as TranslationKey)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="founder-goal-target">{t('founder.onboarding.stage.targetLabel')}</Label>
          <input
            id="founder-goal-target"
            type="number"
            min={0}
            className={inputClass}
            value={goal.target ?? ''}
            disabled={disabled}
            onChange={(e) =>
              onGoal({
                ...goal,
                target: e.target.value === '' ? null : Number(e.target.value),
                label:
                  e.target.value === ''
                    ? null
                    : `${e.target.value} ${goal.metric === 'mrr' ? 'MRR' : goal.metric === 'users' ? 'users' : ''}`.trim(),
              })
            }
          />
        </div>
        {goal.metric === 'mrr' && (
          <div>
            <Label htmlFor="founder-goal-currency">{t('founder.onboarding.stage.currencyLabel')}</Label>
            <select
              id="founder-goal-currency"
              className={inputClass}
              value={goal.currency ?? 'usd'}
              disabled={disabled}
              onChange={(e) => onGoal({ ...goal, currency: e.target.value })}
            >
              {['usd', 'cad', 'eur'].map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Source form (URL / store / text + attestation + paste fallback)
// ---------------------------------------------------------------------------

export function BriefSourceForm({
  stage,
  goal,
  onExtracted,
  disabled,
  briefId,
}: {
  stage?: FounderStage | null;
  goal?: FounderGoal | null;
  onExtracted: (brief: ProductBrief) => void;
  disabled?: boolean;
  /** Analyse into this brief; omitted → the primary brief (created when none). */
  briefId?: string;
}) {
  const { t, locale } = useT();
  const [type, setType] = useState<BriefSourceType>('url');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [paste, setPaste] = useState('');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needs, setNeeds] = useState<ExtractResult | null>(null);

  const hasInput = type === 'text' ? text.trim().length >= 20 : url.trim().length > 3;

  const run = async (withPaste: boolean) => {
    if (busy || disabled) return;
    setError(null);
    setBusy(true);
    try {
      const input = {
        source_type: type,
        url: type === 'text' ? undefined : url.trim(),
        text: type === 'text' ? text.trim() : withPaste ? paste.trim() : undefined,
        // The server is the authority: an unticked box is sent as-is and refused (400).
        ownership_attested: attested,
        stage: stage ?? undefined,
        goal: goal && (goal.target ?? null) !== null ? goal : undefined,
      };
      const res = briefId ? await extractBriefInto(briefId, input) : await extractBrief(input);
      if (res.needs_text) {
        setNeeds(res);
      } else if (res.brief) {
        setNeeds(null);
        setPaste('');
        onExtracted(res.brief);
      }
    } catch (err) {
      setError(founderErrorMessage(err, t, locale));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(false);
  };

  // Enter submits even while the button is disabled, so the server's own refusal
  // (ownership not attested → 400) is surfaced instead of silently ignored.
  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void run(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="brief-source-form" noValidate>
      <div>
        <span className="mb-1 block text-xs font-medium text-muted">{t('founder.source.typeLabel')}</span>
        <div className="flex flex-wrap gap-2">
          {SOURCE_TYPES.map((st) => (
            <ChipToggle key={st} active={type === st} disabled={disabled || busy} onClick={() => setType(st)}>
              {t(`founder.source.types.${st}` as TranslationKey)}
            </ChipToggle>
          ))}
        </div>
      </div>

      {type === 'text' ? (
        <div>
          <Label htmlFor="brief-text">{t('founder.source.textLabel')}</Label>
          <textarea
            id="brief-text"
            rows={3}
            className={inputClass}
            placeholder={t('founder.source.textPlaceholder')}
            value={text}
            disabled={disabled || busy}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      ) : (
        <div>
          <Label htmlFor="brief-url">{type === 'url' ? t('founder.source.urlLabel') : t('founder.source.storeLabel')}</Label>
          <input
            id="brief-url"
            type="text"
            inputMode="url"
            className={inputClass}
            placeholder={type === 'url' ? t('founder.source.urlPlaceholder') : t('founder.source.storePlaceholder')}
            value={url}
            disabled={disabled || busy}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={onEnter}
            autoComplete="url"
          />
        </div>
      )}

      <label className="flex items-start gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={attested}
          disabled={disabled || busy}
          onChange={(e) => setAttested(e.target.checked)}
          data-testid="brief-attest"
        />
        <span>
          {t('founder.source.attest')}
          <span className="mt-0.5 block text-xs text-muted">{t('founder.source.attestHelp')}</span>
        </span>
      </label>
      {!attested && (
        <p className="text-xs text-warning" data-testid="brief-attest-hint">
          {t('founder.source.attestRequired')}
        </p>
      )}

      {error && (
        <div role="alert" className="rounded-token border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="brief-error">
          {error}
        </div>
      )}

      <WithCreditCost feature="product_brief_extract">
        <button
          type="submit"
          className={primaryBtn}
          disabled={disabled || busy || !attested || !hasInput}
          data-testid="brief-extract"
        >
          {busy ? t('founder.source.extracting') : t('founder.source.extract')}
        </button>
      </WithCreditCost>

      {needs?.needs_text && (
        <div className="space-y-2 rounded-token border border-warning/40 bg-warning/10 p-3" data-testid="brief-needs-text">
          <p className="text-sm text-foreground">
            {t(`founder.source.needsText.${needs.reason ?? 'no_text'}` as TranslationKey)}
          </p>
          <Label htmlFor="brief-paste">{t('founder.source.pasteLabel')}</Label>
          <textarea
            id="brief-paste"
            rows={6}
            className={inputClass}
            placeholder={t('founder.source.pastePlaceholder')}
            value={paste}
            disabled={busy}
            onChange={(e) => setPaste(e.target.value)}
          />
          <WithCreditCost feature="product_brief_extract">
            <button
              type="button"
              className={primaryBtn}
              disabled={busy || !attested || paste.trim().length < 20}
              onClick={() => void run(true)}
              data-testid="brief-paste-submit"
            >
              {busy ? t('founder.source.extracting') : t('founder.source.pasteSubmit')}
            </button>
          </WithCreditCost>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Brief editor (editable sections, versioned save)
// ---------------------------------------------------------------------------

interface Draft {
  name: string;
  headline: string;
  sub: string;
  ctaPrimary: string;
  offer: string;
  fromCopy: string;
  fromPricing: string;
  fromTone: string;
  consensus: string;
  voice: string;
  keyMessages: string;
  pricing: BriefPrice[];
}

function draftOf(s: BriefSections): Draft {
  return {
    name: s.name ?? '',
    headline: s.hero?.headline ?? '',
    sub: s.hero?.subheadline ?? '',
    ctaPrimary: s.cta?.primary ?? '',
    offer: s.offer ?? '',
    fromCopy: s.audience?.from_copy ?? '',
    fromPricing: s.audience?.from_pricing ?? '',
    fromTone: s.audience?.from_tone ?? '',
    consensus: s.audience?.consensus ?? '',
    voice: s.tone?.voice ?? '',
    keyMessages: (s.key_messages ?? []).join('\n'),
    pricing: (s.pricing ?? []).map((p) => ({ ...p })),
  };
}

function patchOf(base: BriefSections, d: Draft): Partial<BriefSections> {
  const patch: Partial<BriefSections> = {};
  const nz = (v: string) => (v.trim() ? v.trim() : null);
  if ((base.name ?? '') !== d.name) patch.name = nz(d.name);
  if ((base.hero?.headline ?? '') !== d.headline || (base.hero?.subheadline ?? '') !== d.sub) {
    patch.hero = { headline: nz(d.headline), subheadline: nz(d.sub), verbatim: false };
  }
  if ((base.cta?.primary ?? '') !== d.ctaPrimary) patch.cta = { primary: nz(d.ctaPrimary), verbatim: false };
  if ((base.offer ?? '') !== d.offer) patch.offer = nz(d.offer);
  const a = base.audience ?? {};
  if (
    (a.from_copy ?? '') !== d.fromCopy ||
    (a.from_pricing ?? '') !== d.fromPricing ||
    (a.from_tone ?? '') !== d.fromTone ||
    (a.consensus ?? '') !== d.consensus
  ) {
    patch.audience = {
      from_copy: nz(d.fromCopy),
      from_pricing: nz(d.fromPricing),
      from_tone: nz(d.fromTone),
      consensus: nz(d.consensus),
    };
  }
  if ((base.tone?.voice ?? '') !== d.voice) patch.tone = { voice: d.voice.trim() };
  const km = d.keyMessages.split('\n').map((x) => x.trim()).filter(Boolean);
  if (JSON.stringify(km) !== JSON.stringify(base.key_messages ?? [])) patch.key_messages = km;
  if (JSON.stringify(d.pricing) !== JSON.stringify(base.pricing ?? [])) patch.pricing = d.pricing;
  return patch;
}

export function BriefEditor({
  brief,
  canWrite,
  onSaved,
  compact,
}: {
  brief: ProductBrief;
  canWrite: boolean;
  onSaved: (b: ProductBrief, message: string) => void;
  compact?: boolean;
}) {
  const { t, locale } = useT();
  const s = brief.sections ?? {};
  const [d, setD] = useState<Draft>(() => draftOf(s));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    setD(draftOf(brief.sections ?? {}));
  }, [brief.id, brief.version, brief.sections]);

  const patch = useMemo(() => patchOf(s, d), [s, d]);
  const dirty = Object.keys(patch).length > 0;
  const set = (k: keyof Draft) => (v: string) => setD((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!dirty) {
      setInfo(t('founder.brief.noChanges'));
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await updateBriefById(brief.id, { sections: patch });
      onSaved(res.brief, t('founder.brief.saved', { version: res.brief.version }));
    } catch (err) {
      setError(founderErrorMessage(err, t, locale));
    } finally {
      setBusy(false);
    }
  };

  const field = (id: string, label: string, key: keyof Draft, opts: { rows?: number; tag?: string } = {}) => (
    <div>
      <Label htmlFor={id}>
        {label}
        {opts.tag && <span className="ms-1 rounded bg-surface-2 px-1 text-[10px] uppercase text-muted">{opts.tag}</span>}
      </Label>
      {opts.rows ? (
        <textarea
          id={id}
          rows={opts.rows}
          className={inputClass}
          value={d[key] as string}
          disabled={!canWrite || busy}
          onChange={(e) => set(key)(e.target.value)}
        />
      ) : (
        <input
          id={id}
          className={inputClass}
          value={d[key] as string}
          disabled={!canWrite || busy}
          onChange={(e) => set(key)(e.target.value)}
        />
      )}
    </div>
  );

  const geo = s.geo ?? {};
  const trust = s.trust ?? {};

  return (
    <div className="space-y-4" data-testid="brief-editor">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="rounded-full border border-border px-2 py-0.5" data-testid="brief-version">
          {t('founder.brief.version', { version: brief.version })}
        </span>
        <span className="rounded-full border border-border px-2 py-0.5" data-testid="brief-versions">
          {t('founder.brief.versions', { count: brief.versions })}
        </span>
        <span
          className={`rounded-full border px-2 py-0.5 ${brief.llm_available ? 'border-success/40 text-success' : 'border-warning/40 text-warning'}`}
        >
          {brief.llm_available ? t('founder.brief.llmOn') : t('founder.brief.llmOff')}
        </span>
        {brief.attested_at && (
          <span>{t('founder.brief.attestedOn', { date: new Date(brief.attested_at).toLocaleDateString(locale) })}</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionBox title={t('founder.brief.hero')} testId="brief-hero">
          <div className="space-y-2">
            {field('brief-name', t('founder.brief.name'), 'name')}
            {field('brief-headline', t('founder.brief.heroHeadline'), 'headline', {
              tag: s.hero?.verbatim ? 'verbatim' : undefined,
            })}
            {field('brief-sub', t('founder.brief.heroSub'), 'sub', { rows: 2 })}
          </div>
        </SectionBox>
        <SectionBox title={t('founder.brief.cta')} testId="brief-cta">
          <div className="space-y-2">
            {field('brief-cta', t('founder.brief.ctaPrimary'), 'ctaPrimary', {
              tag: s.cta?.verbatim ? 'verbatim' : undefined,
            })}
            {!!s.cta?.secondary?.length && (
              <p className="text-xs text-muted">
                {t('founder.brief.ctaSecondary')} : {s.cta.secondary.join(' · ')}
              </p>
            )}
            {field('brief-offer', t('founder.brief.offer'), 'offer', { rows: compact ? 2 : 3 })}
          </div>
        </SectionBox>
      </div>

      <SectionBox title={t('founder.brief.pricing')} testId="brief-pricing">
        {d.pricing.length === 0 ? (
          <p className="text-sm text-muted">{t('founder.brief.pricingEmpty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="py-1 pe-2">{t('founder.brief.pricingPlan')}</th>
                  <th className="py-1 pe-2">{t('founder.brief.pricingPrice')}</th>
                  <th className="py-1 pe-2">{t('founder.brief.pricingPeriod')}</th>
                  <th className="py-1">{t('founder.brief.pricingPromo')}</th>
                </tr>
              </thead>
              <tbody>
                {d.pricing.map((p, i) => (
                  <tr key={i} className="border-t border-border" data-testid="brief-price-row">
                    <td className="py-1 pe-2">
                      <input
                        aria-label={t('founder.brief.pricingPlan')}
                        className={inputClass}
                        value={p.plan ?? ''}
                        disabled={!canWrite || busy}
                        onChange={(e) =>
                          setD((prev) => ({
                            ...prev,
                            pricing: prev.pricing.map((x, j) => (j === i ? { ...x, plan: e.target.value } : x)),
                          }))
                        }
                      />
                    </td>
                    <td className="py-1 pe-2 whitespace-nowrap">
                      <span className="font-medium text-foreground">{p.price_text ?? p.price ?? '—'}</span>
                      {p.currency && <span className="ms-1 text-xs text-muted">{p.currency}</span>}
                      {p.currency_assumed && (
                        <span className="ms-1 text-[10px] text-warning">({t('founder.brief.currencyAssumed')})</span>
                      )}
                    </td>
                    <td className="py-1 pe-2 text-muted">{p.period ?? '—'}</td>
                    <td className="py-1 text-muted">{p.promo ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!!s.promos?.length && (
          <p className="mt-2 text-xs text-muted">
            {t('founder.brief.promos')} : {s.promos.join(' · ')}
          </p>
        )}
      </SectionBox>

      <SectionBox title={t('founder.brief.audience')} testId="brief-audience">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          {field('brief-aud-copy', t('founder.brief.fromCopy'), 'fromCopy')}
          {field('brief-aud-pricing', t('founder.brief.fromPricing'), 'fromPricing')}
          {field('brief-aud-tone', t('founder.brief.fromTone'), 'fromTone')}
        </div>
        <div className="mt-2">{field('brief-aud-consensus', t('founder.brief.consensus'), 'consensus')}</div>
      </SectionBox>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SectionBox title={`${t('founder.brief.geo')} (${t('founder.brief.assumed')})`} testId="brief-geo">
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-muted">{t('founder.brief.geoCountry')}</dt>
              <dd>{geo.country ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted">{t('founder.brief.geoCurrency')}</dt>
              <dd>{geo.currency ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted">{t('founder.brief.geoLocale')}</dt>
              <dd>{geo.locale ?? '—'}</dd>
            </div>
          </dl>
          {!!geo.basis?.length && <p className="mt-2 text-[11px] text-muted">{geo.basis.join(' · ')}</p>}
        </SectionBox>
        <SectionBox title={t('founder.brief.trust')} testId="brief-trust">
          <p className="mb-1 text-xs font-medium text-success">{t('founder.brief.trustPresent')}</p>
          <ul className="mb-2 space-y-0.5 text-sm">
            {(trust.present ?? []).map((p) => (
              <li key={p.key}>
                ✓ {tDyn(t, 'founder.trustKeys', p.key)}
                {p.evidence && <span className="ms-1 text-xs text-muted">« {p.evidence} »</span>}
              </li>
            ))}
            {!(trust.present ?? []).length && <li className="text-muted">—</li>}
          </ul>
          <p className="mb-1 text-xs font-medium text-warning">{t('founder.brief.trustMissing')}</p>
          <ul className="space-y-0.5 text-sm" data-testid="brief-trust-missing">
            {(trust.missing ?? []).map((k) => (
              <li key={k}>○ {tDyn(t, 'founder.trustKeys', k)}</li>
            ))}
          </ul>
        </SectionBox>
        <SectionBox title={t('founder.brief.tone')} testId="brief-tone">
          <p className="mb-2 text-sm text-foreground">{(s.tone?.descriptors ?? []).join(' · ') || '—'}</p>
          {field('brief-voice', t('founder.brief.voice'), 'voice', { rows: 2 })}
        </SectionBox>
      </div>

      <SectionBox title={t('founder.brief.keyMessages')}>
        {field('brief-key-messages', t('founder.brief.keyMessages'), 'keyMessages', { rows: 4 })}
      </SectionBox>

      {error && (
        <div role="alert" className="rounded-token border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {info && <p className="text-sm text-muted">{info}</p>}
      {canWrite && (
        <div className="flex justify-end">
          <button type="button" className={primaryBtn} onClick={() => void save()} disabled={busy} data-testid="brief-save">
            {busy ? t('founder.common.saving') : t('founder.brief.save')}
          </button>
        </div>
      )}
    </div>
  );
}

export { secondaryBtn };
